/**
 * Approval Policy
 *
 * Reacts when the user makes a decision on a pending approval card
 * (`pendingApprovalDecision` transitions from `null` to `'approved' | 'rejected'`):
 *
 * - On `approved`: re-subscribes to the conversation's realtime channel (the
 *   `backendPolicy` tore it down on the `interrupt`), then calls the backend
 *   `ResumeAgentRun` command with the original tool name + args and
 *   `approved: true`. The server-side handler mints an approval token bound to
 *   `(toolName, argsHash, conversationId)` and resumes the loop; the resumed
 *   turn streams its chunks + terminal `done` over the SAME channel, which the
 *   fresh subscription now pumps into a new assistant message. The
 *   pending-approval slice is cleared once the resume POST settles.
 * - On `rejected`: clears the pending approval slice. (No backend call, no
 *   subscription — the agent remains paused; the user can issue a new turn.)
 *
 * The component dispatches `approval/approve` or `approval/reject` to trigger
 * this policy; clearing is handled here so the UI dismisses only after the
 * resume request has been sent (success or failure).
 *
 * Re-subscribing here (rather than keeping the initial subscription open across
 * the interrupt) keeps subscription ownership symmetric: each turn owns its
 * subscription, so a `reject` leaks nothing — no subscription is ever created.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, RealtimeSession } from '@bett3r-dev/teselly-client-library';
import { AIAssistantState } from '../state';
import { AIAssistantCommand } from '../commands';
import { subscribeToAssistantStream } from './streamSubscription';

export const createApprovalPolicy = (
  backend: ClientLibraryType,
  realtimeSession: RealtimeSession | null
): Policy<AIAssistantState, AIAssistantCommand> => ({
  name: 'ai-approval',

  shouldReact: ( prev, next ) => {
    // React when a decision is made (null -> approved/rejected)
    return (
      prev.pendingApprovalDecision === null &&
      next.pendingApprovalDecision !== null
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const decision = next.pendingApprovalDecision;
    const pending = next.pendingApproval;

    // Defensive: nothing to do if pending slice is empty
    if ( !pending || !decision ) {
      ctx.dispatch(
        { type: 'aiAssistant/approval/clear', payload: {}},
        { source: 'application' }
      );
      return;
    }

    if ( decision === 'rejected' ) {
      // Reject path: just dismiss the card. The agent run stays interrupted
      // server-side; the user can start a new turn.
      ctx.dispatch(
        { type: 'aiAssistant/approval/clear', payload: {}},
        { source: 'application' }
      );
      return;
    }

    // Approved path: the resumed turn streams over the conversation's realtime
    // channel, but `backendPolicy` unsubscribed on the `interrupt`. Re-establish
    // the subscription — targeting a FRESH assistant loading message so the
    // resumed confirmation renders in its own bubble rather than overwriting the
    // approval question — BEFORE issuing ResumeAgentRun, so no early chunk is
    // missed. Then send ResumeAgentRun; the handler mints an approval token
    // server-side and resumes the loop.
    const timestamp = Date.now();
    const resumedMsgId = `msg-${timestamp}-${Math.random().toString( 36 )
.slice( 2, 8 )}`;

    ctx.dispatch(
      { type: 'aiAssistant/message/addAssistantLoading', payload: { id: resumedMsgId, timestamp }},
      { source: 'application' }
    );
    ctx.dispatch(
      { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: true }},
      { source: 'application' }
    );
    ctx.dispatch(
      { type: 'aiAssistant/processing/setStep', payload: { step: 'formulating' }},
      { source: 'application' }
    );

    const unsubscribe = subscribeToAssistantStream({
      realtimeSession,
      conversationId: pending.conversationId,
      loadingMessageId: resumedMsgId,
      ctx
    });

    try {
      // FIXME:
      // eslint-disable-next-line teselly/no-ungated-command
      const resumeFn = backend.commands.ResumeAgentRun;
      if ( !resumeFn ) {
        throw new Error( 'ResumeAgentRun command is not available on the client library' );
      }

      await resumeFn({
        id: pending.conversationId,
        body: {
          message: {
            approved: true,
            toolName: pending.toolName,
            args: pending.args
          },
          // Tell the backend WHERE to stream the resumed turn. The runner's
          // `processAgentStream` only emits StreamUpdates when
          // `realtimeType && realtimeRef` are set (`canStream`); the initial
          // turn gets these injected by the Conversations->StartAgentRun trigger
          // policy, but ResumeAgentRun is dispatched straight from here, so we
          // must supply them. They MUST match the channel we re-subscribed to
          // above (`subscribeToAssistantStream` -> ('aiAssistant', conversationId)),
          // otherwise the resumed chunks are dropped and the confirmation bubble
          // never renders (TV1-1869).
          realtimeType: 'aiAssistant',
          realtimeRef: pending.conversationId
        }
      });
    } catch ( error ) {
      console.error( '[ai-approval] Error resuming agent run:', error );
      // The resumed turn will never stream — tear down the subscription we just
      // opened and stop the spinner so the UI does not hang.
      unsubscribe();
      ctx.dispatch(
        { type: 'aiAssistant/message/setAssistantResponse', payload: {
          messageId: resumedMsgId,
          content: 'Sorry, I encountered an error completing that action. Please try again.'
        }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
        { source: 'application' }
      );
      ctx.dispatch(
        { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
        { source: 'application' }
      );
    } finally {
      ctx.dispatch(
        { type: 'aiAssistant/approval/clear', payload: {}},
        { source: 'application' }
      );
    }
  }
});
